/**
 * Domain Entity Module: Analytic Accounting
 *
 * This module contains the core domain entities for the analytic accounting system.
 * It implements a hierarchical structure for distributing accounting amounts across
 * multiple levels of analytic plans (cost centers, projects, departments, etc.).
 *
 * Key Concepts:
 * - **Analytic Plan**: A classification scheme (e.g., departments, projects)
 * - **Levels**: Hierarchical nodes within a plan that can have sub-levels
 * - **Distribution**: Splitting amounts across levels (linear, percentage-based, manual)
 * - **Validation**: Ensuring child amounts sum to parent amounts
 *
 * @module analitic_accounting/domain/Company
 */

import { round } from "mathjs";
import { Third, ThirdType } from "./Third";

/**
 * Type of account matching for analytic plan references
 */
export enum AnalyticReferenceType {
    /** Exact account codes match */
    BY_ACCOUNTS = 'by_accounts',
    /** Match by account prefix (first N digits) */
    BY_ACCOUNT_GROUP = 'by_account_group'
}

/**
 * Defines which analytic plans should be automatically assigned
 * based on account codes and/or third parties (customers/suppliers).
 *
 * This allows automatic selection of analytic plans when creating
 * accounting entries based on business rules.
 */
export interface AnalyticPlanReference {
    /** Unique identifier for the reference */
    id: number;
    /** Type of account matching */
    account_rule_type: AnalyticReferenceType;
    /** List of analytic plans that should be applied */
    analytic_plans: AnalyticPlan[];
    /**
     * Account matching rules (depends on account_rule_type):
     * - If BY_ACCOUNTS: exact account codes (e.g., ["4300", "4310", "5700"])
     * - If BY_ACCOUNT_GROUP: account prefixes (e.g., ["43", "57"] matches all 43xx and 57xx)
     */
    account_rules: string[];
    /**
     * Third parties (customers/suppliers) this reference applies to.
     * If empty, applies to all thirds
     */
    thirds: Third[];
    /** Additional references/tags for categorization */
    references: string[];
    /** Whether this reference applies to supplier transactions */
    apply_to_suppliers: boolean;
    /** Whether this reference applies to customer transactions */
    apply_to_customers: boolean;
}

// Re-export ThirdType for convenience
export { ThirdType };

/**
 * Base class for analytic levels containing common properties.
 * Provides code and name properties shared by all level types.
 */
class LevelBase {
    /** Unique code identifier for the level */
    code: string
    /** Human-readable name/description of the level */
    name: string
}


/**
 * Template Level - represents a level definition in a company's analytic plan template.
 * Used as a template/blueprint that defines the structure and default percentages.
 * This is the "definition" side, not the transactional side.
 *
 * Example: A company defines a "Departments" plan with levels:
 * - Sales (40%)
 * - Marketing (30%)
 * - IT (30%)
 */
export class Level extends LevelBase {
    /** Child levels in the hierarchy */
    levels: Level[]
    /** Default percentage for automatic distribution (e.g., 30 for 30%) */
    percent?: number
    /** UI flag indicating if this level should be visible in UI (checkbox selection) */
    ui_visible?: boolean

    /**
     * Creates a template level
     * @param code - Unique identifier for the level
     * @param name - Display name for the level
     * @param levels - Array of child levels
     * @param percent - Default percentage for distribution (optional)
     * @param ui_visible - UI visibility flag (optional)
     */
    constructor(code: string, name: string, levels: Level[] = [], percent?: number, ui_visible?: boolean) {
        super();
        this.code = code;
        this.name = name;
        this.levels = levels;
        this.percent = percent;
        this.ui_visible = ui_visible;
    }
}

/**
 * Transaction Line Level - represents an analytic level with actual monetary amounts.
 * Used in accounting transactions/lines where amounts are distributed across levels.
 * This is the "transactional" side where actual money is allocated.
 *
 * Example: A $1000 expense is distributed across departments:
 * - Sales: $400
 * - Marketing: $300
 * - IT: $300
 */
export class LineLevel extends LevelBase {
    /** Monetary amount allocated to this level */
    amount: number
    /** Child levels with their amounts */
    levels: LineLevel[]
    /** UI flag indicating validation error (amount doesn't match sum of children) */
    ui_error: boolean
    /** UI flag indicating if this level should be visible in UI (checkbox selection) */
    ui_visible?: boolean

    /**
     * Creates a transactional line level
     * @param code - Unique identifier for the level
     * @param name - Display name for the level
     * @param levels - Array of child line levels
     * @param amount - Monetary amount allocated (defaults to null)
     */
    constructor(code: string, name: string, levels: LineLevel[] = [], amount: number = null) {
        super();
        this.code = code;
        this.name = name;
        this.levels = levels;
        this.amount = amount;
        this.ui_error = false
    }

    /**
     * Converts the line level to a plain JavaScript object for serialization.
     * Recursively converts all child levels as well.
     * @returns Plain object representation suitable for API calls or storage
     */
    to_primitive(): any {
        return {
            code: this.code,
            name: this.name,
            amount: this.amount,
            ui_visible: this.ui_visible,
            levels: this.levels.map(level => level.to_primitive())
        };
    }
}

/**
 * Base class for analytic plans containing common properties.
 */
export class AnalyticPlanBase {
    /** Unique code identifier for the plan */
    code: string
    /** Human-readable name of the plan */
    name: string
}

/**
 * Template Analytic Plan - defines the structure of an analytic classification scheme.
 * This is the company's configuration/template defining how amounts can be distributed.
 *
 * Example plans:
 * - "Departments" plan with levels: Sales, Marketing, IT
 * - "Projects" plan with levels: Project A, Project B, Project C
 * - "Locations" plan with levels: Office1, Office2, Warehouse
 */
export class AnalyticPlan extends AnalyticPlanBase {
    /** Top-level hierarchy of classification levels */
    levels: Level[]

    /**
     * Creates a template analytic plan
     * @param code - Unique identifier for the plan
     * @param name - Display name for the plan
     * @param levels - Top-level levels in the plan hierarchy
     */
    constructor(code: string, name: string, levels: Level[] = []) {
        super();
        this.code = code;
        this.name = name;
        this.levels = levels;
    }
}

/**
 * Transaction Line Analytic Plan - represents an analytic plan with actual amounts.
 * Used in accounting lines/transactions to distribute a total amount across levels.
 *
 * Example: A $1000 invoice line is distributed via "Departments" plan:
 * - Total: $1000
 * - Sales: $400
 * - Marketing: $300
 * - IT: $300
 */
export class LineAnalyticPlan extends AnalyticPlanBase {
    /** Top-level line levels with amounts */
    levels: LineLevel[]
    /** Total amount to be distributed across levels */
    total_amount: number
    /** UI flag indicating validation error (sum of levels ≠ total_amount) */
    ui_error: boolean

    /**
     * Creates a transactional line analytic plan
     * @param code - Plan identifier
     * @param name - Plan display name
     * @param levels - Line levels with amounts
     * @param total_amount - Total amount to distribute
     */
    constructor(code: string, name: string, levels: LineLevel[], total_amount: number) {
        super();
        this.code = code;
        this.name = name;
        this.levels = levels;
        this.total_amount = parseFloat(total_amount.toString())
        this.ui_error = false
    }

    /**
     * Converts the plan to a plain JavaScript object for serialization.
     * @returns Plain object representation
     */
    to_primitive(): any {
        return {
            code: this.code,
            name: this.name,
            total_amount: this.total_amount,
            levels: this.levels.map(level => level.to_primitive())
        };
    }

    /**
     * Validates that amounts at each level sum correctly to their parent amounts.
     * Sets ui_error flags on levels where the sum of children doesn't match parent.
     *
     * Validation Rules:
     * - If all child amounts are null/NaN, skip validation (not yet filled in)
     * - Sum of child amounts must equal parent amount (within 0.000001 tolerance)
     * - Errors propagate down to all children of an invalid parent
     * - Top-level validation checks against total_amount
     */
    validate_level_amounts(): void {
        
        console.log("blackraven - Company.ts - validate_level_amounts");
        const set_ui_error = (l,b) => {
            l.ui_error = b
            if(b)
                this.ui_error = true
            for (const level of l.levels) {
                set_ui_error(level, b)
            }
        }

        const validate_level_amounts_rec = (l: LineAnalyticPlan|LineLevel, amount_field: string) => {
            if(l.levels.length==0)
                return
            let all_nulls = true
            for (const level of l.levels) {
                console.log("blackraven - Company.ts - validate_level_amounts_rec: ", level.amount);
                if(!Number.isNaN(level.amount))
                    all_nulls = false
            }
            if(all_nulls)
                return
            let total = 0;
            for (const level of l.levels) {
                if(level.amount)
                    total += parseFloat(level.amount.toString());
            }
            if (Math.abs(total - l[amount_field]) > 1e-6) {
                set_ui_error(l, true)
            }
            else{
                for (const level of l.levels) {
                    validate_level_amounts_rec(level, 'amount')
                }
            }
        }
        set_ui_error(this,false)
        validate_level_amounts_rec(this, 'total_amount')
    }

    /**
     * Applies linear automatic distribution - divides total_amount equally across all levels.
     * Handles rounding to cents and ensures the sum equals total_amount exactly.
     *
     * Example: $1000 across 3 levels = $333.33, $333.33, $333.34
     */
    set_linear_automatic_distribution(): void {
        splitAmount(this.total_amount, this.levels)
    }

    /**
     * Applies percentage-based automatic distribution using percentages from the original plan template.
     * Each level receives an amount proportional to its defined percentage.
     * Handles rounding adjustments to ensure exact total match.
     *
     * @param originalPlan - The template plan containing percentage definitions
     */
    set_percentage_automatic_distribution(originalPlan: AnalyticPlan): void {
        splitAmountByPercentageFromPlan(this.total_amount, this.levels, originalPlan.levels)
    }

    /**
     * Applies equal distribution among only the selected (ui_visible) levels.
     * Used when automatic percentage is enabled but account has no predefined plan.
     * Distributes total amount equally among selected levels only.
     */
    set_equal_distribution_for_selected(): void {
        splitAmountEquallyForSelected(this.total_amount, this.levels)
    }

    /**
     * Updates the total amount and re-validates all level amounts.
     * @param newTotalAmount - The new total to distribute
     */
    update_total_amount(newTotalAmount: number): void {
        this.total_amount = parseFloat(newTotalAmount.toString())
        this.validate_level_amounts()
    }
}

/**
 * Splits an amount equally across all levels (linear distribution).
 * Works in cents to avoid floating-point errors, ensures exact sum.
 * Recursively distributes amounts to sub-levels as well.
 *
 * Algorithm:
 * 1. Convert amount to cents (multiply by 100, round)
 * 2. Divide cents equally, distribute remainder to first N levels
 * 3. Convert back to currency units (divide by 100)
 * 4. Apply recursively to children
 *
 * Example: 100.00 across 3 levels = 33.33, 33.33, 33.34
 *
 * @param amount - Total amount to split
 * @param levels - Array of levels to distribute across
 */
function splitAmount(amount: number, levels: LineLevel[]): void {
    let n = levels.length
    if(n==0) return
    // Treballem en cèntims per evitar errors de punt flotant
    const totalCents = Math.round(amount * 100);

    const base = Math.floor(totalCents / n);     // cèntims per part
    let remainder = totalCents - base * n;       // cèntims sobrants a repartir

    const parts = Array.from({ length: n }, (_, i) => {
      const cents = base + (i < remainder ? 1 : 0); // reparteix l'excés pels primers elements
      return cents / 100;
    });
    let level;
    for(let i=0;i<n;i++){
        level = levels[i]
        level.amount = parts[i]
        splitAmount(level.amount,level.levels)

    }
  }

/**
 * Splits an amount equally across only the selected (ui_visible = true) levels.
 * Non-selected levels receive null (no amount).
 * Used when automatic percentage is enabled but no predefined percentages exist.
 *
 * @param amount - Total amount to distribute
 * @param levels - Array of levels (only those with ui_visible = true receive amounts)
 */
function splitAmountEquallyForSelected(amount: number, levels: LineLevel[]): void {
    if (levels.length === 0) return;

    // Count how many levels are selected (ui_visible = true)
    const selectedLevels = levels.filter(level => level.ui_visible === true);
    const selectedCount = selectedLevels.length;

    if (selectedCount === 0) {
        // No levels selected, set all to null
        levels.forEach(level => {
            level.amount = null;
        });
        return;
    }

    // Work in cents to avoid floating-point errors
    const totalCents = Math.round(amount * 100);
    const base = Math.floor(totalCents / selectedCount);
    let remainder = totalCents - base * selectedCount;

    let selectedIndex = 0;
    for (let i = 0; i < levels.length; i++) {
        const level = levels[i];

        if (level.ui_visible === true) {
            // This level is selected, give it a portion
            const cents = base + (selectedIndex < remainder ? 1 : 0);
            level.amount = cents / 100;
            selectedIndex++;

            // Recursively distribute to children if they exist
            if (level.levels && level.levels.length > 0) {
                splitAmountEquallyForSelected(level.amount, level.levels);
            }
        } else {
            // This level is not selected, set amount to null
            level.amount = null;
        }
    }
}

/**
 * Distributes an amount according to percentage values stored in level.amount properties.
 * NOTE: This function treats level.amount as a percentage, not a currency amount.
 * Works in cents for precision, adjusts rounding errors on last level.
 *
 * @param amount - Total amount to distribute
 * @param levels - Levels where amount property contains percentages (e.g., 30 for 30%)
 *
 * @deprecated This function is less commonly used. Consider using splitAmountByPercentageFromPlan instead.
 */
function splitAmountByPercentage(amount: number, levels: LineLevel[]): void {
    if(levels.length == 0) return;

    // Treballem en cèntims per evitar errors de punt flotant
    const totalCents = Math.round(amount * 100);

    // Calculate amounts based on percentages
    const parts: number[] = [];
    let allocatedCents = 0;

    for(let i = 0; i < levels.length; i++) {
        const level = levels[i];
        if (level.amount !== null && level.amount !== undefined) {
            const percentage = level.amount / 100; // Convert percentage to decimal
            const cents = Math.round(totalCents * percentage);
            parts.push(cents / 100);
            allocatedCents += cents;
        } else {
            parts.push(0);
        }
    }

    // Adjust for rounding errors - distribute the difference to the last non-zero level
    const remainingCents = totalCents - allocatedCents;
    if (remainingCents !== 0) {
        for(let i = levels.length - 1; i >= 0; i--) {
            if (parts[i] > 0) {
                parts[i] += remainingCents / 100;
                break;
            }
        }
    }

    // Apply the calculated amounts and recursively process sub-levels
    for(let i = 0; i < levels.length; i++) {
        const level = levels[i];
        level.amount = parts[i];
        splitAmountByPercentage(level.amount, level.levels);
    }
}

/**
 * Distributes an amount using percentages from the template plan (most accurate method).
 * This is the recommended approach for percentage-based distribution.
 *
 * Algorithm:
 * 1. Leaf nodes calculate amount = root_total * (percent / 100)
 * 2. Parent nodes sum their children's amounts
 * 3. Small rounding differences (≤ 1 cent) adjusted on last leaf
 * 4. Adjustments propagate up to parents
 *
 * Rules:
 * - Leaf amounts calculated from ROOT total (not parent amount) for consistency
 * - Parent amounts = sum of children (calculated, not from percentage)
 * - Only the last leaf receives rounding adjustment
 * - Maximum adjustment is 0.01 (1 cent)
 *
 * @param amount - Total amount to distribute
 * @param lineLevels - Transaction levels to receive amounts
 * @param originalLevels - Template levels containing percentage definitions
 * @param rootAmount - Internal parameter for recursive calls (do not set manually)
 */
function splitAmountByPercentageFromPlan(amount: number, lineLevels: LineLevel[], originalLevels: Level[], rootAmount?: number): void {
    if(lineLevels.length == 0 || originalLevels.length == 0) return;

    // Use rootAmount if provided (for nested calls), otherwise use amount
    const baseAmount = rootAmount !== undefined ? rootAmount : amount;

    // First pass: calculate all amounts from root total
    // Leaf nodes get calculated amounts, parents will be recalculated as sum of children
    function buildLevels(lineLevels: LineLevel[], originalLevels: Level[]): void {
        for(let i = 0; i < lineLevels.length && i < originalLevels.length; i++) {
            const lineLevel = lineLevels[i];
            const originalLevel = originalLevels[i];

            if (originalLevel.percent !== null && originalLevel.percent !== undefined) {
                const percentage = originalLevel.percent / 100;
                lineLevel.amount = round(baseAmount * percentage, 2);
            } else {
                lineLevel.amount = null;
            }

            // Recursively process children
            buildLevels(lineLevel.levels, originalLevel.levels);

            // If has children, parent amount is sum of children
            if (lineLevel.levels.length > 0) {
                const childrenSum = lineLevel.levels.reduce((sum, child) => {
                    return sum + (child.amount !== null ? child.amount : 0);
                }, 0);
                lineLevel.amount = round(childrenSum, 2);
            }
        }
    }

    // Build the tree with amounts
    buildLevels(lineLevels, originalLevels);

    // Only apply adjustment at the root level (when rootAmount is undefined)
    if (rootAmount === undefined) {
        // Collect all leaf nodes
        function collectLeafNodes(levels: LineLevel[], leaves: LineLevel[]): void {
            for (const level of levels) {
                if (level.levels.length === 0) {
                    leaves.push(level);
                } else {
                    collectLeafNodes(level.levels, leaves);
                }
            }
        }

        const allLeaves: LineLevel[] = [];
        collectLeafNodes(lineLevels, allLeaves);

        // Calculate total from leaves
        const leafSum = allLeaves.reduce((sum, leaf) => {
            return sum + (leaf.amount !== null ? leaf.amount : 0);
        }, 0);
        const computedTotal = round(leafSum, 2);

        // Calculate adjustment needed
        const diff = round(amount - computedTotal, 2);

        // Apply adjustment to last leaf if difference is small (max 1 cent)
        if (diff !== 0 && Math.abs(diff) <= 0.01 && allLeaves.length > 0) {
            const lastLeaf = allLeaves[allLeaves.length - 1];
            if (lastLeaf.amount !== null) {
                lastLeaf.amount = round(lastLeaf.amount + diff, 2);

                // Propagate the adjustment up to parents
                function updateParents(levels: LineLevel[]): void {
                    for (const level of levels) {
                        if (level.levels.length > 0) {
                            updateParents(level.levels);
                            const childrenSum = level.levels.reduce((sum, child) => {
                                return sum + (child.amount !== null ? child.amount : 0);
                            }, 0);
                            level.amount = round(childrenSum, 2);
                        }
                    }
                }
                updateParents(lineLevels);
            }
        }
    }
}
  

/**
 * Represents an accounting account with its associated analytic plans.
 * Defines which analytic plans are applicable to a specific account code.
 *
 * Example: Account "6000" (Purchases) might have plans:
 * - "Departments" plan
 * - "Projects" plan
 * But NOT "Locations" plan (if locations only apply to expenses, not purchases)
 */
export class Account {
    /** Account code (e.g., "6000", "7000") */
    code: string;
    /** Analytic plans applicable to this account */
    plans: AnalyticPlan[];

    /**
     * Creates an account with its applicable plans
     * @param code - Account code identifier
     * @param plans - Array of analytic plans that can be used with this account
     */
    constructor(code: string, plans: AnalyticPlan[] = []) {
        this.code = code;
        this.plans = plans;
    }
}

/**
 * Represents the configuration settings for analytics plans within a company.
 * Each company can define its own configuration by specifying the values
 * for the available parameters.
 */
export class AnalyticsPlanConfiguration {
    
    /**
     * Whether analytics plans are enabled
     */
    is_analytic_plan: boolean;
    
    /**
     * Whether analytics plans are allowed across all accounts.
     */
    is_allow_analytics_plans_all_accounts: boolean;
    
    /**
     * Whether manual input distribution is allowed.
     */
    is_manual_input_distribution: boolean;
    
    /**
     * Whether linear automatic distribution is enabled.
     */
    is_linear_automatic_distribution: boolean;
    
    /**
     * Whether automatic percentage-based distribution is enabled.
     */
    is_automatic_percentage_distribution: boolean;

    /**
     * Whether single analytic level is enabled.
     */
    is_single_analytic_level: boolean;

    /**
     * Whether only defined levels are allowed.
     */
    is_only_defined_levels: boolean;

    constructor({
            is_analytic_plan = false,
            is_allow_analytics_plans_all_accounts = false,
            is_manual_input_distribution = false,
            is_linear_automatic_distribution = false,
            is_automatic_percentage_distribution = false,
            is_single_analytic_level = false,
            is_only_defined_levels = false,
        }: {
            is_analytic_plan?: boolean;
            is_allow_analytics_plans_all_accounts?: boolean;
            is_manual_input_distribution?: boolean;
            is_linear_automatic_distribution?: boolean;
            is_automatic_percentage_distribution?: boolean;
            is_single_analytic_level?: boolean;
            is_only_defined_levels?: boolean;
     } = {}) {
        this.is_analytic_plan = is_analytic_plan;
        this.is_allow_analytics_plans_all_accounts = is_allow_analytics_plans_all_accounts;
        this.is_manual_input_distribution = is_manual_input_distribution;
        this.is_linear_automatic_distribution = is_linear_automatic_distribution;
        this.is_automatic_percentage_distribution = is_automatic_percentage_distribution;
        this.is_single_analytic_level = is_single_analytic_level;
        this.is_only_defined_levels = is_only_defined_levels;
    }
}

/**
 * Company aggregate root - contains all analytic accounting configuration for a company.
 * This is the main entry point for all analytic accounting operations.
 *
 * A company defines:
 * - What analytic plans exist (departments, projects, locations, etc.)
 * - Which plans apply to which accounts
 * - Distribution rules (manual, linear automatic, percentage-based)
 * - Automatic analytic plan references based on account codes and third parties
 */
export class Company {
    /** Company identifier */
    id: number;
    /** All analytic plan templates available to this company */
    plans: AnalyticPlan[];
    /** Account-specific plan assignments (which plans apply to which accounts) */
    accounts_plans: Account[];
    /** Company-wide analytic accounting configuration/preferences */
    analytics_plan_configuration: AnalyticsPlanConfiguration;
    /** Automatic analytic plan references based on account codes and third parties */
    analytic_plan_references: AnalyticPlanReference[];

    /**
     * Creates a company with its analytic accounting configuration
     * @param id - Company identifier
     * @param plans - Array of available analytic plans
     * @param accounts_plans - Account-specific plan assignments
     * @param analytics_plan_configuration - Configuration settings (uses defaults if not provided)
     * @param analytic_plan_references - Automatic analytic plan references (defaults to empty array)
     */
    constructor(id: number, plans: AnalyticPlan[] = [], accounts_plans: Account[] = [], analytics_plan_configuration?: AnalyticsPlanConfiguration, analytic_plan_references: AnalyticPlanReference[] = []) {
        this.id = id;
        this.plans = plans;
        this.accounts_plans = accounts_plans;
        this.analytics_plan_configuration = analytics_plan_configuration ?? new AnalyticsPlanConfiguration();
        this.analytic_plan_references = analytic_plan_references;

        console.log("Company Constructor", analytics_plan_configuration);
    }

    /**
     * Determines if percentage column should be shown in the UI for a given account.
     *
     * Returns true only if:
     * - Company has automatic percentage distribution enabled
     * - Account code is provided
     *
     * Note: The percentage row will be shown even if the account doesn't have a plan
     * assigned initially, allowing users to manually enter percentage values.
     *
     * @param accountCode - The account code to check
     * @returns true if percentage column should be displayed
     */
    shouldShowPercentForAccount(accountCode: string | null | undefined): boolean {
        // Check if is_automatic_percentage_distribution is enabled
        if (!this.analytics_plan_configuration?.is_automatic_percentage_distribution) {
            return false;
        }

        // Check if account code is provided
        if (!accountCode) {
            return false;
        }

        // Show percentage row for all accounts when automatic percentage distribution is enabled
        // This allows users to manually enter percentage values even if no plan is initially assigned
        return true;
    }
}

/**
 * Represents a single accounting line/transaction with analytic plan distributions.
 * Each line can have up to 2 analytic plans applied simultaneously.
 *
 * Example: An invoice line for $1000 on account "6000" might have:
 * - plan_1: "Departments" distributing across Sales/Marketing/IT
 * - plan_2: "Projects" distributing across Project A/B/C
 *
 * This allows multi-dimensional analysis (by department AND by project).
 */
export class Line {
    /** Line identifier (can be null for new lines) */
    id: number | null;
    /** Account code this line applies to */
    account: string;
    /** Line number within the document/transaction */
    line_number: number;
    /** First analytic plan distribution (optional) */
    plan_1: LineAnalyticPlan | null;
    /** Second analytic plan distribution (optional) */
    plan_2: LineAnalyticPlan | null;
    /** UI flag - whether plan_1 details popup is shown */
    ui_show_plan_1: boolean;
    /** UI flag - whether plan_2 details popup is shown */
    ui_show_plan_2: boolean;

    /**
     * Creates an accounting line with analytic plans
     * @param id - Line identifier
     * @param account - Account code
     * @param plan_1 - First analytic plan (optional)
     * @param plan_2 - Second analytic plan (optional)
     * @param line_number - Line number in document (optional)
     */
    constructor(id: number, account: string, plan_1: LineAnalyticPlan | null = null, plan_2: LineAnalyticPlan | null = null, line_number: number | null = null) {
        console.log("blackraven Company.ts - Line Constructor: ", id, line_number, plan_1, plan_2);
        this.id = id;
        this.account = account;
        this.line_number = line_number;
        this.plan_1 = plan_1;
        this.plan_2 = plan_2;
        this.ui_show_plan_1 = false;
        this.ui_show_plan_2 = false;
    }

    /**
     * Converts the line to a plain JavaScript object for serialization.
     * @returns Plain object representation with all plans converted to primitives
     */
    to_primitive(): any {
        let res = {}
        res['id'] = this.id;
        res['account'] = this.account
        res['line_number'] = this.line_number;
        res['plan_1'] = null;
        res['plan_2'] = null;
        if(this.plan_1)
            res['plan_1'] = this.plan_1.to_primitive();
        if(this.plan_2)
            res['plan_2'] = this.plan_2.to_primitive();
        return res
    }
}